(() => {
  'use strict';
  if (customElements.get('gift-options')) return;
  // v1.2: HTMLFormElement named controls can shadow form.id (the theme uses name="id").
  const formId = form => form?.getAttribute('id');
  const visibleQuantity = form => {
    const input = document.getElementById(`${formId(form)}-quantity`);
    return input?.form === form && !input.disabled ? input : null;
  };
  const states = new Map(); // Page memory only: no localStorage or cookies for personal messages.
  const transactions = new Map();
  const root = () => window.Shopify?.routes?.root || '/';
  const uuid = () => window.crypto.randomUUID();

  class GiftOptions extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.options = [...this.querySelectorAll('[data-gift-kind]')];
      this.message = this.querySelector('[data-message]');
      const previous = states.get(this.dataset.stateKey);
      if (previous) {
        this.options.forEach(input => { input.checked = !!previous[input.dataset.giftKind] && input.dataset.ready === 'true'; });
        this.message.value = previous.message.slice(0, this.message.maxLength);
      }
      this.addEventListener('input', () => this.update(), {signal: this.abort.signal});
      this.addEventListener('change', () => this.update(), {signal: this.abort.signal});
      document.addEventListener('input', event => {
        if (formId(event.target.form) === this.dataset.formId && event.target.name === 'quantity') this.update();
      }, {signal: this.abort.signal});
      document.addEventListener('change', event => {
        if (formId(event.target.form) === this.dataset.formId && event.target.name === 'quantity') this.update();
      }, {signal: this.abort.signal});
      this.querySelector('fieldset').disabled = false;
      this.querySelector('[data-loading]').hidden = true;
      this.update();
    }
    disconnectedCallback() { this.abort?.abort(); }
    get selected() { return this.options.filter(input => input.checked); }
    get form() { return document.getElementById(this.dataset.formId); }
    update() {
      const card = this.options.find(input => input.dataset.giftKind === 'card')?.checked || false;
      this.querySelector('[data-message-panel]').hidden = !card;
      this.message.disabled = !card;
      this.message.required = card && this.message.dataset.required === 'true';
      this.message.setCustomValidity('');
      this.querySelector('[data-counter]').textContent = `${this.message.value.length} / ${this.message.maxLength} tegn`;
      const perUnit = this.selected.reduce((total, input) => total + Number(input.dataset.price), 0);
      const quantityField = visibleQuantity(this.form) || this.form?.elements.namedItem('quantity');
      const quantity = Number(quantityField?.value || 1);
      const money = new Intl.NumberFormat(this.dataset.locale || 'da', {
        style: 'currency', currency: this.dataset.currency || 'DKK'
      });
      this.querySelector('[data-total]').textContent = this.selected.length
        ? `Tillæg pr. stk.: ${money.format(perUnit / 100)}` + (Number.isInteger(quantity) && quantity > 1 ? ` · ${quantity} stk.: ${money.format(perUnit * quantity / 100)}` : '')
        : '';
      const state = {message: this.message.value};
      this.options.forEach(input => { state[input.dataset.giftKind] = input.checked; });
      states.set(this.dataset.stateKey, state);
    }
    error(text, review = false) {
      const box = this.querySelector('[data-error]');
      box.textContent = text;
      box.hidden = false;
      this.querySelector('[data-cart-link]').hidden = !review;
      box.focus();
    }
    buildItems(data, group) {
      const id = Number(data.get('id'));
      const quantity = Number(data.get('quantity') || 1);
      if (!Number.isSafeInteger(id) || id <= 0 || !Number.isInteger(quantity) || quantity < 1) {
        throw new Error('Vælg en gyldig variant og et gyldigt antal.');
      }
      const properties = Object.create(null);
      for (const [name, value] of data.entries()) {
        const match = name.match(/^properties\[(.+)\]$/);
        if (!match) continue;
        if (value instanceof File) {
          if (value.size) throw new Error('Gavetilvalg kan ikke kombineres med filupload i denne løsning.');
          continue;
        }
        properties[match[1]] = value;
      }
      // These keys belong exclusively to this block; remove stale form values.
      ['Gaveindpakning', 'Kort', 'Korttekst', '_gavegruppe', '_gavetilvalg'].forEach(key => delete properties[key]);
      properties._gavegruppe = group;
      properties._gavetilvalg = 'main';
      const parent = {id, quantity, properties};
      const sellingPlan = data.get('selling_plan');
      if (sellingPlan) parent.selling_plan = sellingPlan;
      const items = [parent];
      for (const input of this.selected) {
        if (input.dataset.ready !== 'true' || input.disabled) throw new Error('Et af dine gavetilvalg er ikke tilgængeligt.');
        const kind = input.dataset.giftKind;
        properties[kind === 'wrapping' ? 'Gaveindpakning' : 'Kort'] = 'Ja';
        if (kind === 'card' && this.message.value.trim()) properties.Korttekst = this.message.value;
        if (input.dataset.paid !== 'true') continue;
        if (this.dataset.bundle === 'true') throw new Error('Betalte gavetilvalg kan ikke bruges med dette bundleprodukt.');
        const addonId = Number(input.dataset.variantId);
        if (!Number.isSafeInteger(addonId) || addonId < 1 || addonId === id || items.some(item => item.id === addonId)) {
          throw new Error('Gavetilvalgene er ikke korrekt opsat. Kontakt butikken.');
        }
        items.push({id: addonId, quantity, parent_id: id, properties: {
          _gavegruppe: group, _gavetilvalg: kind, 'Til produkt': this.dataset.productTitle
        }});
      }
      return items;
    }
    async add(form) {
      const existing = transactions.get(formId(form));
      if (existing) {
        if (existing.review) this.error('Kontrollér kurven, før du forsøger at tilføje igen.', true);
        return;
      }
      this.querySelector('[data-error]').hidden = true;
      this.querySelector('[data-cart-link]').hidden = true;
      this.update();
      if (this.message.required && !this.message.value.trim()) this.message.setCustomValidity('Skriv en hilsen til kortet.');
      if (this.message.value.length > this.message.maxLength && !this.message.disabled) this.message.setCustomValidity('Din hilsen er for lang.');
      if (!form.reportValidity()) return;
      let items;
      const group = uuid();
      try {
        const data = new FormData(form);
        // Read the actual selector before bypassing the theme's target-phase quantity sync.
        const quantity = visibleQuantity(form);
        if (quantity) data.set('quantity', quantity.value);
        items = this.buildItems(data, group);
      }
      catch (error) { this.error(error.message); return; }
      transactions.set(formId(form), {review: false});
      const controls = [...form.elements].filter(element => element.matches('button, input[type="submit"]'));
      const disabled = controls.map(element => element.disabled);
      controls.forEach(element => { element.disabled = true; element.setAttribute('aria-busy', 'true'); });
      this.setAttribute('aria-busy', 'true');
      this.querySelector('fieldset').disabled = true;
      let posted = false;
      let statusKnown = false;
      let postOK = false;
      let complete = false;
      let review = false;
      let description = '';
      let addedResult;
      let cartContent;
      const sectionsToBundle = ['variant-added'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true, detail: {sections: sectionsToBundle}
      }));
      try {
        posted = true;
        const response = await fetch(`${root()}cart/add.js`, {
          method: 'POST', credentials: 'same-origin',
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
          body: JSON.stringify({items, sections: [...new Set(sectionsToBundle)].join(','), sections_url: `${root()}cart`}), signal: AbortSignal.timeout(25000)
        });
        statusKnown = true;
        postOK = response.ok;
        const result = await response.json();
        addedResult = result;
        if (!response.ok) description = typeof result.description === 'string' ? result.description : 'Tilføjelsen kunne ikke gennemføres.';
      } catch (_) {
        description = 'Forbindelsen blev afbrudt under tilføjelsen.';
      }
      try {
        const response = await fetch(`${root()}cart.js`, {credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000)});
        if (!response.ok) throw new Error();
        const cart = await response.json();
        cartContent = cart;
        const lines = cart.items.filter(line => line.properties?._gavegruppe === group);
        const parent = lines.find(line => line.properties?._gavetilvalg === 'main');
        complete = items.every(item => {
          const actual = lines.filter(line => Number(line.variant_id) === item.id && line.properties?._gavetilvalg === item.properties._gavetilvalg);
          return actual.length === 1 && actual[0].quantity === item.quantity &&
            (!item.parent_id || (parent && actual[0].parent_relationship?.parent_key === parent.key));
        });
        review = lines.length > 0 || !statusKnown || postOK;
      } catch (_) { review = posted; }
      if (complete) {
        transactions.delete(formId(form));
        this.removeAttribute('aria-busy');
        this.querySelector('fieldset').disabled = false;
        controls.forEach((element, index) => {
          element.disabled = disabled[index];
          element.removeAttribute('aria-busy');
        });
        // Match ProductForm's public event contract. The existing drawer owns rendering,
        // bundle layout and opening; do not replace drawer HTML or dispatch cart:refresh.
        cartContent.sections = addedResult?.sections;
        const addedItems = Array.isArray(addedResult?.items) ? addedResult.items
          : cartContent.items.filter(line => line.properties?._gavegruppe === group);
        form.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true, detail: {items: addedItems, cart: cartContent}
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true, detail: {baseEvent: 'variant:add', cart: cartContent}
        }));
        return;
      }
      // A 422 can still add available quantities. Never silently retry a partial/uncertain add.
      if (review) transactions.set(formId(form), {review: true});
      else transactions.delete(formId(form));
      this.removeAttribute('aria-busy');
      this.querySelector('fieldset').disabled = false;
      controls.forEach((element, index) => { element.disabled = disabled[index]; element.removeAttribute('aria-busy'); });
      this.error((description ? `${description} ` : '') + (review
        ? 'Kurven kan være ændret. Åbn kurven og kontrollér varer, tilvalg og antal, før du fortsætter.'
        : 'Intet blev tilføjet. Kontrollér dine valg, og prøv igen.'), review);
    }
  }
  customElements.define('gift-options', GiftOptions);
  // Capture before the theme's form handler, including sticky buttons associated via form="…".
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const widget = [...document.querySelectorAll('gift-options')].find(element => element.dataset.formId === formId(form));
    if (!widget || (!transactions.has(formId(form)) && !widget.selected.length)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void widget.add(form);
  }, true);
  window.addEventListener('pagehide', () => states.clear());
  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      transactions.clear();
      document.querySelectorAll('gift-options[aria-busy]').forEach(element => {
        element.removeAttribute('aria-busy');
        // Reload avoids restoring a submitted form with disabled purchase buttons.
        window.location.reload();
      });
    }
  });
})();
